// Incubation models - converted from C# models

export interface IncubationSession {
  id: string;
  originalAsk: string;
  state: WorkflowState;
  lastExecutedStep: WorkflowStepType;
  createdAt: Date;
  lastUpdatedAt: Date;
  messages: ChatMessage[];
  
  // Workflow results
  systemNarrative?: SystemNarrative | string;
  technicalLandscape?: TechnicalLandscape;
  systemRequirements?: SystemRequirements;
  projectPhases?: ProjectPhases;
  dataSchema?: DataSchema;
  projectBuild?: ProjectBuild;
  generatedCode: CodeFile[];
  finalDocumentation?: FinalDocumentation;
}

export interface SessionParametersModel {
  originalAsk: string;
  projectName?: string;
  targetPlatform?: string;
  preferredStack?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  stepType?: WorkflowStepType;
  metadata?: Record<string, any>;
}

export enum WorkflowState {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled'
}

export enum WorkflowStepType {
  None = 'None',
  SystemNarrative = 'SystemNarrative',
  TechnicalLandscape = 'TechnicalLandscape',
  SystemRequirements = 'SystemRequirements',
  ProjectPhases = 'ProjectPhases',
  DataSchema = 'DataSchema',
  ProjectBuild = 'ProjectBuild',
  FinalDocumentation = 'FinalDocumentation'
}

export interface WorkflowStepResult {
  success: boolean;
  summary: string;
  data: any;
  generatedCode?: CodeFile[];
  metadata?: Record<string, any>;
}

export interface SystemNarrative {
  systemVision: string;
  systemDescription: string;
  corePurpose: string;
  targetUser: string;
  keyFeatures: string[];
  businessGoals: string[];
  technicalObjectives: string[];
  successMetrics: string[];
  keyChallenges: string[];
  stakeholders: Record<string, string>;
}

export interface TechnicalLandscape {
  platform: string;
  techStack: string[];
  systemFeatures: string[];
  userTypes: string[];
  integrations: string[];
  securityRequirements: string[];
  performanceRequirements: string[];
  architectureComponents: Record<string, string>;
  deploymentEnvironments: string[];
  scalabilityConsiderations: string[];
}

export interface SystemRequirements {
  epics: Epic[];
  userStories: UserStory[];
  functionalRequirements: FunctionalRequirement[];
  nonFunctionalRequirements: NonFunctionalRequirement[];
  acceptanceCriteria: AcceptanceCriteria[];
}

export interface Epic {
  id: string;
  title: string;
  description: string;
  priority: number;
  userStoryIds: string[];
  selected?: boolean; // For UI state
}

export interface UserStory {
  id: string;
  epicId: string;
  asA: string;
  iWant: string;
  soThat: string;
  storyText: string;
  storyPoints: number;
  priority: string;
  acceptanceCriteria: string[];
  selected?: boolean; // For UI state
}

export interface FunctionalRequirement {
  id: string;
  category: string;
  description: string;
  priority: string;
}

export interface NonFunctionalRequirement {
  id: string;
  category: string;
  description: string;
  metric: string;
  target: string;
}

export interface AcceptanceCriteria {
  id: string;
  userStoryId: string;
  given: string;
  when: string;
  then: string;
}

export interface ProjectPhases {
  sprints: ProjectSprint[];
  milestones: Milestone[];
  timeline: ProjectTimeline;
  resourcePlan: ResourcePlan;
  riskAssessment: RiskAssessment;
}

export interface ProjectSprint {
  sprintNumber: number;
  name: string;
  goal: string;
  startDate: Date;
  endDate: Date;
  userStoryIds: string[];
  tasks: string[];
  estimatedStoryPoints: number;
}

export interface Milestone {
  name: string;
  description: string;
  targetDate: Date;
  deliverables: string[];
  dependencies: string[];
}

export interface ProjectTimeline {
  projectStartDate: Date;
  estimatedEndDate: Date;
  totalWeeks: number;
  phaseTimelines: Record<string, DateRange>;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface ResourcePlan {
  teamMembers: TeamMember[];
  roleAllocation: Record<string, number>;
  estimatedBudget: number;
}

export interface TeamMember {
  role: string;
  skillLevel: string;
  allocationPercentage: number;
}

export interface RiskAssessment {
  risks: Risk[];
  mitigations: Mitigation[];
}

export interface Risk {
  id: string;
  description: string;
  impact: string;
  probability: string;
  category: string;
}

export interface Mitigation {
  riskId: string;
  strategy: string;
  responsibleParty: string;
}

export interface DataSchema {
  entities: DataEntity[];
  relationships: Relationship[];
  databaseSchema: DatabaseSchema;
  migrations: DataMigration[];
}

export interface DataEntity {
  id: string;
  name: string;
  description: string;
  fields: DataField[];
  indexes: string[];
}

export interface DataField {
  name: string;
  dataType: string;
  isRequired: boolean;
  isUnique: boolean;
  defaultValue?: string;
  description: string;
}

export interface Relationship {
  fromEntity: string;
  toEntity: string;
  relationshipType: string;
  description: string;
}

export interface DatabaseSchema {
  databaseType: string;
  connectionString: string;
  tables: string[];
  views: string[];
  storedProcedures: string[];
}

export interface DataMigration {
  version: string;
  description: string;
  upScript: string;
  downScript: string;
}

export interface ProjectBuild {
  projectStructure: ProjectStructure;
  modules: CodeModule[];
  buildConfiguration: BuildConfiguration;
  deploymentConfiguration: DeploymentConfiguration;
}

export interface ProjectStructure {
  rootPath: string;
  directories: Record<string, string>;
  configFiles: string[];
}

export interface CodeModule {
  name: string;
  type: string;
  path: string;
  dependencies: string[];
  configuration: Record<string, string>;
}

export interface BuildConfiguration {
  buildTool: string;
  buildSteps: string[];
  environmentVariables: Record<string, string>;
}

export interface DeploymentConfiguration {
  targetEnvironment: string;
  deploymentMethod: string;
  serverConfiguration: Record<string, string>;
  preDeploymentSteps: string[];
  postDeploymentSteps: string[];
}

export interface CodeFile {
  fileName: string;
  filePath: string;
  content: string;
  language: string;
  generatedAt: Date;
}

export interface FinalDocumentation {
  projectOverview: string;
  userManual: string;
  technicalDocumentation: string;
  apiDocumentation: string;
  deploymentGuide: string;
  maintenanceGuide: string;
  additionalSections: DocumentationSection[];
}

export interface DocumentationSection {
  title: string;
  content: string;
  order: number;
}